import React, { ReactElement, useCallback, useState } from 'react'
import { Button, Col, FormGroup, Input, Label, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap'
import { Role, RolesList } from '../../backoffice/dtos/authentication/role';
import { useCreateUserMutation, useGetAllRolesQuery, useGetUserRequestQuery, useUpdateUserMutation } from '../../backoffice/apis/userApi';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import { useTranslation } from 'react-i18next';
import { user } from '../../backoffice/dtos/authentication/user';
import HierarchyRoleEditModal from '../Settings/Users/HierarchyRoleEditModal';
import { Loading } from '../views/Loading';
import { useGetGroupsQuery } from '../../backoffice/apis/groupsAPI';
import { ErrorComponent } from '../views/ErrorComponent';
import { Group } from '../../backoffice/dtos/groups/group';
import GroupEditModal from './GroupsEditModal';
import PasswordCopyModal from './PasswordCopyModal';

const UserEditModal = (
    {
        isOpen,
        close,
        userId
    }: {
        isOpen: boolean;
        close: () => void;
        userId: number | undefined;
    }
) => {
    const { t } = useTranslation(['user_info', 'common'])
    const { data: roles, isLoading: rolesLoading, error: roleError } = useGetAllRolesQuery(undefined, { skip: !isOpen, refetchOnMountOrArgChange: true })
    const { data: groups, isLoading: groupsAreLoading, error: groupsError } = useGetGroupsQuery(undefined, { skip: !isOpen, refetchOnMountOrArgChange: true })

    let body: JSX.Element
    if (rolesLoading || groupsAreLoading) {
        body = <Loading className="m-4" />
    } else if(roleError || groupsError || !roles || !groups) {
        body =  <ErrorComponent error='Failed to fetch data.' />
    } else {
        if (userId) {
            body =  <UserEditWithUser isOpen={isOpen} close={close} userId={userId} roles={roles} groups={groups} />
        } else {
            body =  <UserEditInnerModal isOpen={isOpen} close={close} user={undefined} roles={roles} groups={groups} />
        }
    }

    return (
        <>
            <Modal size='xl' isOpen={isOpen} toggle={close}>
                <ModalHeader toggle={close}>{t('edit', { ns: 'common' })}</ModalHeader>
                {body}
            </Modal>
        </>
    )
}

function UserEditWithUser(
    {
        isOpen,
        close,
        userId,
        groups,
        roles
    }: {
        isOpen: boolean;
        close: () => void;
        userId: number;
        groups: Group[];
        roles: RolesList;
    }
) {
    const { data: currentUser, isLoading, error } = useGetUserRequestQuery(userId, {skip: !isOpen, refetchOnMountOrArgChange: true})

    if (isLoading) {
        return <Loading className='m-4' />
    } else if(error || !currentUser) {
        return <ErrorComponent error='Failed to fetch data.' />
    } else {
        return <UserEditInnerModal isOpen={isOpen} close={close} user={currentUser} roles={roles} groups={groups} />
    }
}

function UserEditInnerModal(
    {
        isOpen,
        close,
        user: currentUser,
        groups,
        roles
    }: {
        isOpen: boolean;
        close: () => void;
        user: user | undefined;
        groups: Group[];
        roles: RolesList;
    }
) {
    const { t } = useTranslation(['user_info', 'common'])
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<user>(currentUser ?? { id: 0, userName: "", email: "", groups: [], roles: [], hierarchyRoles: [], isDeleted: false });
    const [modalData, setModalData] = useState<{ isOpen: boolean, role: Role | undefined, roleType: 'roles' | 'hierarchyRoles' }>(
        { isOpen: false, role: undefined, roleType: 'roles' }
    );
    const [groupModalData, setGroupModalData] = useState<{ isOpen: boolean, group: Group | undefined }>(
        { isOpen: false, group: undefined }
    );
    const [password, setPassword] = useState<string>();

    const [updateUser] = useUpdateUserMutation();
    const [createUser] = useCreateUserMutation();

    const handleSubmit = useCallback(async () => {
        try {
            setIsLoading(true);
            if (user.id > 0) {
                await updateUser(user).unwrap()

                close()
            } else {
                const passwordDto = await createUser(user).unwrap()
                setPassword(passwordDto.password);
            }
        } catch (e: any) {
            toast(e.data)
        } finally {
            setIsLoading(false);
        }
    }, [user])

    const askToSave = useCallback(() => {
        confirmAlert({
            title: t("Save", { ns: "common" }),
            message: t("Are you sure you want to save the user?"),
            buttons: [{ label: t('yes', { ns:'common' }), onClick: handleSubmit }, { label: t('no', { ns: 'common' }) }]
        })
    }, [handleSubmit])

    const deleteRole = useCallback((role: Role, roleType: 'roles' | 'hierarchyRoles') => {
        if (!user)
            return;

        var updatedRoles = [];

        for (const item of user[roleType]) {
            if (role.name === item.name)
                continue;

            updatedRoles.push(item);
        }


        setUser({ ...user, [roleType]: updatedRoles });
    }, [user]);

    const deleteGroup = useCallback((groupId: number) => {
        var updatedGroups = [];

        for (const item of user.groups) {
            if (groupId === item.id)
                continue;

            updatedGroups.push(item);
        }

        setUser({ ...user, groups: updatedGroups });
    }, [user])


    const addUpdateRole = useCallback((role: Role, roleType: 'roles' | 'hierarchyRoles') => {
        if (!user)
            return;

        var updatedRoles = [];

        for (const item of user[roleType]) {
            if (role.name === item.name)
                continue;

            updatedRoles.push(item);
        }

        updatedRoles.push(role);

        setUser({ ...user, [roleType]: updatedRoles });
    }, [user]);

    const addUpdateGroup = useCallback((group: Group) => {
        var updatedGroups = [];

        for (const item of user.groups) {
            if (group.id === item.id)
                continue;

            updatedGroups.push(item);
        }

        updatedGroups.push(group);

        setUser({ ...user, groups: updatedGroups });
    }, [user]);

    const closeCopyModal = useCallback(() => {
        setPassword(undefined);
        close()
    }, []);

    let groupsUi: JSX.Element
    if (user) {
        groupsUi = (
            <>
                {
                    user.groups.map(
                        (role, index) => (
                            <ListGroupItem key={index} >
                                <Row divider>
                                    <Col lg={4} >{role.description}</Col>
                                    <Col className='d-flex justify-content-end'>
                                        <Button color="danger" className='w-40 ' onClick={() => deleteGroup(role.id)} disabled={isLoading}>
                                            {isLoading && <Spinner size="sm" className='me-2'/>} <i className='fa fa-trash fa-lg'></i>
                                        </Button>
                                    </Col>
                                </Row>
                            </ListGroupItem>
                        )
                    )
                }
            </>
        )
    } else {
        groupsUi = <Loading className="m-2" />
    }

    let rolesUi: JSX.Element
    if (user) {
        rolesUi = (
            <>
                {
                    user.roles.map(
                        (role, index) => (
                            <ListGroupItem key={index} >
                                <Row divider>
                                    <Col lg={1} className='bold-text'>{role.level}</Col>
                                    <Col lg={3} >{role.name}</Col>
                                    <Col className='d-flex justify-content-end'>
                                        <Button color="danger" className='w-40 ' onClick={() => deleteRole(role, "roles")} disabled={isLoading}>
                                            {isLoading && <Spinner size="sm" className='me-2'/>} <i className='fa fa-trash fa-lg'></i>
                                        </Button>
                                    </Col>
                                </Row>
                            </ListGroupItem>
                        )
                    )
                }
            </>
        )
    } else {
        rolesUi = <Loading className="m-2" />
    }

    let hierarchyRolesUi: JSX.Element
    if (user) {
        hierarchyRolesUi = (
            <>
                {
                    user.hierarchyRoles.map(
                        (role, index) => (
                            <ListGroupItem key={index} >
                                <Row divider>
                                    <Col lg={1} className='bold-text'>{role.level}</Col>
                                    <Col lg={3} >{role.name}</Col>
                                    <Col lg={1} className="d-flex justify-content-center">
                                        <Input type="switch" name='isPrimary' checked={role.isPrimary === true} />
                                    </Col>
                                    <Col lg={2} className="d-flex justify-content-center">
                                        <Input type="switch" name='informViaEmail' checked={role.informViaEmail === true} />
                                    </Col>
                                    <Col lg={3} >{role.informScheduleCron}</Col>
                                    <Col className='d-flex justify-content-end'>
                                        <Button color="primary " className='w-40 mx-1' onClick={() => setModalData({ isOpen: true, role: role, roleType: 'hierarchyRoles' })} disabled={isLoading}>
                                            {isLoading && <Spinner size="sm" className='me-2'/>} <i className='fa fa-edit fa-lg'></i>
                                        </Button>
                                        <Button color="danger" className='w-40  mx-1' onClick={() => deleteRole(role, "hierarchyRoles")} disabled={isLoading}>
                                            {isLoading && <Spinner size="sm" className='me-2'/>} <i className='fa fa-trash fa-lg'></i>
                                        </Button>
                                    </Col>
                                </Row>
                            </ListGroupItem>
                        )
                    )
                }
            </>
        )
    } else {
        hierarchyRolesUi = <Loading className="m-4" />
    }

    return (
        <>
            <ModalBody>
                <Row>
                    <Col>
                        <div className="form-floating m-2">
                            <Input
                                type='text'
                                style={{ fontWeight: "700", fontSize: "1rem" }}
                                name='userName'
                                value={user.userName}
                                disabled={user.id > 0}
                                onChange={(it) => setUser({...user, userName: it.target.value})}
                            />
                            <Label style={{ fontWeight: "900", fontSize: "1.2rem" }} for="userName">{t('Username', { ns: 'common' })}</Label>
                        </div>
                    </Col>
                    <Col>
                        <div className="form-floating m-2 ">
                            <Input
                                type='text'
                                style={{ fontWeight: "700", fontSize: "1rem" }}
                                name='email'
                                value={user.email}
                                disabled={user.id > 0}
                                onChange={(it) => setUser({...user, email: it.target.value})}
                            />
                            <Label style={{ fontWeight: "900", fontSize: "1.2rem" }} for="email">Email</Label>
                        </div>
                    </Col>
                </Row>

                <Row className='mb-2 mt-4'>
                    <Col lg="2">
                        <h4>{t('Departments', { ns: 'common' })}</h4>
                    </Col>
                    <Col className="d-flex  justify-content-end">
                        <Button color="primary" onClick={() => setGroupModalData({ isOpen: true, group: undefined })} disabled={isLoading}>
                            {isLoading && <Spinner size="sm" className='me-2'/>} <i className='fa fa-plus fa-lg me-2'></i>{t('Add', { ns: 'common' })}
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <ListGroup>
                        <ListGroupItem>
                            <Row >
                                <Col lg={4}>{t('Name', { ns: 'common' })}</Col>
                                <Col lg={2}></Col>
                            </Row>
                        </ListGroupItem>
                        {
                            groupsUi
                        }
                    </ListGroup>
                </Row>

                <Row className='mb-2 mt-4'>
                    <Col lg="2">
                        <h4>{t("Roles", { ns: 'common' })}</h4>
                    </Col>
                    <Col className="d-flex  justify-content-end">
                        <Button color="primary" onClick={() => setModalData({ isOpen: true, role: undefined, roleType: 'roles'})} disabled={isLoading}>
                            {isLoading && <Spinner size="sm" className='me-2'/>} <i className='fa fa-plus fa-lg me-2'></i>{t('Add', { ns: 'common' })}
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <ListGroup>
                        <ListGroupItem>
                            <Row >
                                <Col lg={1}>{t('Level', { ns: 'common' })}</Col>
                                <Col lg={2}>{t('Name', { ns: 'common' })}</Col>
                                <Col lg={2}></Col>
                            </Row>
                        </ListGroupItem>
                        {
                            rolesUi
                        }
                    </ListGroup>
                </Row>

                <Row className='mb-2 mt-4'>
                    <Col lg="3">
                        <h4>{t('Hierarchy Roles', { ns: 'common' })}</h4>
                    </Col>
                    <Col className="d-flex  justify-content-end">
                        <Button color="primary" onClick={() => setModalData({ isOpen: true, role: undefined, roleType: 'hierarchyRoles'})} disabled={isLoading}>
                            {isLoading && <Spinner size="sm" className='me-2'/>} <i className='fa fa-plus fa-lg me-2'></i>{t('Add', { ns: 'common' })}
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <ListGroup>
                        <ListGroupItem>
                            <Row >
                                <Col lg={1}>{t('Level', { ns: 'common' })}</Col>
                                <Col lg={3}>{t('Name', { ns: 'common' })}</Col>
                                <Col lg={1}>{t('Primary', { ns: 'common' })}</Col>
                                <Col lg={2}>{t('Inform Via Email', { ns: 'common' })}</Col>
                                <Col lg={3}>{t('Inform Schedule', { ns: 'common' })}</Col>
                            </Row>
                        </ListGroupItem>
                        {
                            hierarchyRolesUi
                        }
                    </ListGroup>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="success" onClick={askToSave} disabled={isLoading}>
                    {isLoading && <Spinner size="sm" className='me-2'/>} {t('Save', { ns: 'common' })}
                </Button>
            </ModalFooter>
            {
                groupModalData.isOpen &&
                    <GroupEditModal
                        isOpen={groupModalData.isOpen}
                        groups={groups}
                        close={() => setGroupModalData({ isOpen: false, group: undefined })}
                        addGroup={addUpdateGroup}
                    />
            }
            {
                modalData.isOpen &&
                    <HierarchyRoleEditModal
                        isOpen={modalData.isOpen}
                        roles={roles}
                        roleToEdit={modalData.role}
                        close={() => setModalData({ isOpen: false, role: undefined, roleType: 'roles' })}
                        submit={addUpdateRole}
                        roleType={modalData.roleType}
                    />
            }
            {
                password &&
                    <PasswordCopyModal
                        password={password}
                        toggle={closeCopyModal}
                    />
            }
        </>
    )
}

export default UserEditModal
